import theme from "assets/theme";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { getRelatedAlerts } from "shared/helpers/shared.helpers";
import { ALERT_ACCESSOR_TEAM } from "web/constants";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import PersonList from "webComponentsScreens/teams-list/PersonList.component";
import RelatedAlerts from "webComponentsShared/RelatedAlerts.component";
import { AlertContext } from "webContext/Alert.context";
import { OrganizationContext } from "webContext/Organization.context";

const classes = StyleSheet.create({
  root: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "95%",
    height: "90%",
    overflow: "hidden",
  },
  column: {
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    minWidth: "50%",
  },
  section: {
    width: "100%",
  },
});

function areArraysContentDifferent(a, b) {
  const setA = new Set(a);
  const setB = new Set(b);

  if (setA.size !== setB.size) {
    return true;
  }

  for (const el of setA) {
    if (!setB.has(el)) {
      return true;
    }
  }

  for (const el of setB) {
    if (!setA.has(el)) {
      return true;
    }
  }

  return false;
}

export default function UpsertTeamForm(props) {
  const {
    team = null,
    setCanSave,
    setTeamToAdd,
    setTeamPersonBatchList,
  } = props;

  const isEditing = !!team;
  const { organizationColor, personList } = useContext(OrganizationContext);
  const { sortedAllAlerts, getAlerts } = useContext(AlertContext);

  const [teamName, setTeamName] = useState(team?.name || "");
  const [teamPersonIds, setTeamPersonIds] = useState(() =>
    team?._associations?.TeamPersons
      ? team._associations.TeamPersons.map((tp) => tp.personId)
      : [],
  );

  useEffect(() => {
    if (!sortedAllAlerts || sortedAllAlerts.length === 0) {
      getAlerts();
    }
  }, [getAlerts, sortedAllAlerts]);

  const relatedAlerts = useMemo(
    () => getRelatedAlerts(ALERT_ACCESSOR_TEAM, team?.teamId, sortedAllAlerts),
    [sortedAllAlerts, team?.teamId],
  );

  const initialName = useRef(team?.name || "");
  const initialPersons = useRef(Array.from(teamPersonIds));

  const stateClasses = useMemo(
    () =>
      StyleSheet.create({
        title: {
          fontSize: 18,
          fontWeight: theme.font.weight.semibold,
          color: organizationColor,
          paddingBottom: 10,
          paddingTop: 16,
        },
      }),
    [organizationColor],
  );

  useEffect(() => {
    setTeamToAdd((prev) => ({
      ...(prev || {}),
      teamId: team?.teamId,
      name: teamName,
    }));
  }, [teamName, team, setTeamToAdd]);

  useEffect(() => {
    if (isEditing) {
      const nameChanged = initialName.current !== teamName;
      const personsChanged = areArraysContentDifferent(
        teamPersonIds,
        initialPersons.current,
      );

      if (nameChanged || personsChanged) {
        setCanSave(true);
        setTeamPersonBatchList && setTeamPersonBatchList(teamPersonIds);
      } else {
        setCanSave(false);
      }
    } else {
      const canSaveNow = teamName.length > 0;
      setCanSave(canSaveNow);
      setTeamPersonBatchList && setTeamPersonBatchList(teamPersonIds);
    }
  }, [teamName, teamPersonIds, isEditing, setCanSave, setTeamPersonBatchList]);

  return (
    <View style={classes.root}>
      <View style={classes.column}>
        <View style={classes.section}>
          <LabelInput
            label="Name"
            required
            value={teamName}
            onChangeText={setTeamName}
          />
        </View>
        {team && <RelatedAlerts alerts={relatedAlerts} entityType="Team" />}
        <Text style={stateClasses.title}>Team Members</Text>
        <PersonList
          personList={personList}
          teamId={team?.teamId}
          checkedPersons={teamPersonIds}
          setCheckedPersons={setTeamPersonIds}
          setCanSave={setCanSave}
        />
      </View>
    </View>
  );
}
